import React, { useEffect, useRef } from "react";
import juice from "juice";

// Helper function to parse and clean content value
const parseContent = (value) => {
  if (!value) return "";

  // If it's not a string, return as-is
  if (typeof value !== "string") {
    return value;
  }

  // Try to parse as JSON first
  try {
    const parsed = JSON.parse(value);
    // If parsing succeeds and returns a string, use it
    if (typeof parsed === "string") {
      return parsed;
    }
    // If it's an object with content/html property, extract it
    if (parsed && typeof parsed === "object") {
      return parsed.content || parsed.html || parsed.text || value;
    }
  } catch (e) {
    // Not JSON, continue with string processing
  }

  // Handle escaped characters in the string
  // Replace escaped newlines with actual newlines, then convert to HTML breaks
  let cleaned = value
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\");

  // If the content looks like HTML, return it as-is
  if (cleaned.includes("<") && cleaned.includes(">")) {
    return cleaned;
  }

  // Otherwise, convert newlines to <br> tags for HTML display
  return cleaned.replace(/\n/g, "<br>");
};

// RedactorEditor component - wrapper for Redactor WYSIWYG editor
// Following the official React setup: https://imperavi.com/redactor/docs/get-started/react-setup/
export const RedactorEditor = ({
  value,
  onChange,
  placeholder = "Enter content...",
}) => {
  const textAreaRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Keep the ref in sync so the Redactor closure always calls the latest callback
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let editor;

    const waitForRedactor = () => {
      if (typeof window.Redactor === "undefined") {
        setTimeout(waitForRedactor, 100);
        return;
      }
      requestAnimationFrame(createEditor);
    };

    const createEditor = () => {
      if (!textAreaRef.current || editorRef.current) return;

      const cleanedContent = juice(parseContent(value));

      const opts = {
        content: cleanedContent,
        plugins: [
          "ai",
          "paddingDropdown",
          "email",
          "alignment",
          "anchor",
          "blockbackground",
          "blockborder",
          "blockclass",
          "blockcode",
          "blockcolor",
          "blockfontsize",
          "blockid",
          "blockspacing",
          "clips",
          "collapse",
          "counter",
          "definedlinks",
          "emoji",
          "filelink",
          "fontcolor",
          "fontfamily",
          "fontsize",
          "fullscreen",
          "highlighter",
          "icons",
          "imageresize",
          "limiter",
          "math",
          "mention",
          "mergetag",
          "print",
          "snippets",
          "specialchars",
          "tableofcontents",
          "templates",
          "textdirection",
          "typographer",
          "variable",
        ],
        context: true,
        pathbar: true,
        click: true,
        ai: {
          text2: {
            url: "https://ai_redactor.hellorubric.com?type=request",
            endpoint: "https://api.openai.com/v1/chat/completions",
            model: "gpt-4o",
            stream: false,
          },
          text: {
            url: "https://ai_redactor.hellorubric.com?type=stream",
            endpoint: "https://api.openai.com/v1/chat/completions",
            model: "gpt-4o",
            stream: true,
          },
          image: {
            url: "https://ai_redactor.hellorubric.com?type=image",
            endpoint: "https://api.openai.com/v1/images/generations",
            model: "dall-e-3",
          },
        },

        image: {
          upload: `https://uploadfile.hellorubric.com?revolve=true`,
        },
        toolbar: {
          sticky: true,
        },
        subscribe: {
          "editor.change": function () {
            try {
              let html = "";
              if (typeof this.app.editor.getContent === "function") {
                html = this.app.editor.getContent();
              } else if (typeof this.app.content?.getHtml === "function") {
                html = this.app.content.getHtml();
              } else if (typeof editorRef.current?.getContent === "function") {
                html = editorRef.current.getContent();
              } else if (typeof editorRef.current?.get === "function") {
                html = editorRef.current.get();
              }
              if (onChangeRef.current) {
                onChangeRef.current(html);
              }
            } catch (e) {
              console.warn("Error getting editor content on change:", e);
            }
          },
          "block.select": function (block) {
            this.selectedBlock = block;
            console.log("block.select", block);
            // this.openDropdown(block);
          },
          "block.deselect": function () {
            console.log("block.deselect");
            this.app.dropdown.close();
          },
          "block.set": function (event) {
            let instance = event.get("instance");
            console.log("block.set", instance);
          },
          "block.create": function (block) {
            console.log("block.create", block);
          },
          "block.delete": function (block) {
            console.log("block.delete", block);
          },
          "context.open": function () {
            console.log("context.open");
          },
          "context.close": function () {
            console.log("context.close");
          },
          "dropdown.open": function (event) {
            console.log("dropdown.open", event);
            let dropdown = event.get("dropdown");
            // let name = dropdown.getName();
          },
          "dropdown.close": function (event) {
            console.log("dropdown.close", event);
            let dropdown = event.get("dropdown");
            // let name = dropdown.getName();
          },
          "form.before.create": function (event) {
            console.log("form.before.create", event);
          },
        },
      };

      editor = window.Redactor(textAreaRef.current, opts);
      editorRef.current = editor;
    };

    const timeoutId = setTimeout(waitForRedactor, 100);

    // Cleanup function - destroy editor on unmount
    // Following the official pattern: https://imperavi.com/redactor/docs/get-started/react-setup/
    return () => {
      clearTimeout(timeoutId);
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Update content when value prop changes externally
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      try {
        // Parse and clean the new content value
        const cleanedContent = parseContent(value);

        // Try different API methods that Redactor might use
        let currentContent = "";
        if (typeof editorRef.current.get === "function") {
          currentContent = editorRef.current.get();
        } else if (typeof editorRef.current.getContent === "function") {
          currentContent = editorRef.current.getContent();
        }

        // Only update if the content is different to avoid unnecessary updates
        if (currentContent !== cleanedContent) {
          if (typeof editorRef.current.set === "function") {
            editorRef.current.set(cleanedContent);
          } else if (typeof editorRef.current.setContent === "function") {
            editorRef.current.setContent(cleanedContent);
          } else {
            // Try setting via content property
            try {
              editorRef.current.opts.content = cleanedContent;
            } catch (e) {
              console.warn("Could not update Redactor content:", e);
            }
          }
        }
      } catch (error) {
        console.error("Error updating Redactor content:", error);
      }
    }
  }, [value]);

  return <textarea id="redactor-editor" ref={textAreaRef}></textarea>;
};
